import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../../../app/hooks";
import { Loading } from "../../../../../components/Loading";
import Select from "../../../../../components/input/Select";
import { SimpleOption } from "../../../../../components/input/SimpleListbox";
import {
	useEssayDraftHistoryReportQuery,
	WriterEssayDraftHistoryViewModel,
} from "../../../shared/progress/essayDraftHistory/essayDraftHistoryReportApiSlice";
import {
	getAverageScoreIncrease,
	getDraftCount,
	getFirstPromptOption,
	getLastDraftAverage,
	isConstructedResponse,
	getPromptOptions,
} from "../../../shared/progress/essayDraftHistory/DraftHistoryUtil";
import { EssayDraftHistorySummary } from "../../../shared/progress/essayDraftHistory/TableUtil";
import EssayDraftHistoryTable from "./EssayDraftHistoryTable";
import EssayScoresByDraftChart from "./EssayDraftHistoryChart";

export const ESSAY_DRAFT_HISTORY_SECTION = "essay-draft-history";

export default function EssayDraftHistory() {
	const courseId = useAppSelector((state) => state.user.course?.id);
	const result = useEssayDraftHistoryReportQuery({ courseId });
	const data = result.data;

	const [promptFilter, setPromptFilter] = useState<number>(0);
	const [writerFilter, setWriterFilter] = useState<number>(0);
	const handlePromptChange = (promptId: number) => {
		setPromptFilter(promptId);
	};
	const handleWriterChange = (writerId: number) => {
		setWriterFilter(writerId);
	};

	const summaries = useMemo(
		() => toTableSummary(data, writerFilter, promptFilter),
		[data, writerFilter, promptFilter]
	);
	const filteredChartData = useMemo(
		() => toChartSummary(data, writerFilter, promptFilter),
		[data, writerFilter, promptFilter]
	);

	// Set prompt filter to be first prompt available from dataset.
	useEffect(() => {
		setPromptFilter(getFirstPromptOption(data ?? []));
	}, [data]);

	return (
		<section id={ESSAY_DRAFT_HISTORY_SECTION} className="flex flex-col mt-8">
			<h2>Essay Draft History</h2>
			<p className="mt-4">
				The report below displays the scores for all scored drafts for a student's most
				recent essay by prompt, the average score increase between each draft, and how far
				away from the class average each student is on their last scored draft. Expand a
				student in the table to see their scores across all drafts. You can use the prompt
				and writer filters, to view this report for specific students and prompts.
			</p>
			<Loading loading={result.isLoading} />
			{!result.isLoading && (
				<>
					<div className="flex flex-col mt-4 gap-2 w-full md:flex-row md:gap-4">
						<Select
							value={promptFilter}
							setValue={(v) => handlePromptChange(v as number)}
							label="Prompt Filter"
							simpleListBoxId="promptFilter"
							options={getPromptOptions(data)}
							fieldClasses="w-1/4"
						/>
						<Select
							value={writerFilter}
							setValue={(v) => handleWriterChange(v as number)}
							label="Writer Filter"
							simpleListBoxId="writerFilter"
							options={getWriterOptions(data, promptFilter)}
							fieldClasses="w-1/5"
						/>
					</div>
					<EssayDraftHistoryTable data={summaries} />
					<EssayScoresByDraftChart
						data={filteredChartData}
						isAllWriters={writerFilter == 0}
						isConstructedResponse={isConstructedResponse(filteredChartData)}
					/>
				</>
			)}
		</section>
	);
}

function getWriterOptions(
	data: WriterEssayDraftHistoryViewModel[] | undefined,
	promptFilter: number
): SimpleOption[] {
	const allWriters: SimpleOption = { value: 0, label: "All Writers" };
	if (!data) {
		return [allWriters];
	}

	const map = new Map<number, string>();
	data.forEach((writer) => {
		if (writer.essays.some((essay) => essay.promptId == promptFilter)) {
			map.set(writer.writerId, writer.writerFormalFullName);
		}
	});

	const options = Array.from(map.entries()).map(([value, label]) => ({ value, label }));
	return [allWriters, ...options.sort((a, b) => a.label.localeCompare(b.label))];
}

// Return filtered and summarized data for table.
function toTableSummary(
	data: WriterEssayDraftHistoryViewModel[] | undefined,
	writerFilter: number,
	promptFilter: number
): EssayDraftHistorySummary[] {
	if (!data) {
		return [];
	}

	const filteredWriters =
		writerFilter == 0
			? data
			: data.filter((writer) => Number(writer.writerId) === Number(writerFilter));

	return filteredWriters
		.map((writer) => {
			const essays = writer.essays ?? [];
			const filteredEssays =
				promptFilter == 0
					? essays
					: essays.filter((essay) => Number(essay.promptId) === Number(promptFilter));

			const essayAverageIncreases = filteredEssays.map((essay) =>
				getAverageScoreIncrease(essay.drafts ?? [])
			);

			const draftAverageIncrease =
				essayAverageIncreases.length > 0
					? Number(
							(
								essayAverageIncreases.reduce((acc, val) => acc + val, 0) /
								essayAverageIncreases.length
							).toFixed(1)
						)
					: 0;

			// Store the score of the last draft of each essay
			const lastDraftScores = filteredEssays
				.flatMap((e) => e.drafts)
				.flatMap((d) => d.draftScore);
			const lastScore = lastDraftScores[lastDraftScores.length - 1];

			return {
				writerId: writer.writerId,
				writerFormalFullName: writer.writerFormalFullName,
				draftCount: getDraftCount(filteredEssays, promptFilter),
				averageIncrease: draftAverageIncrease,
				differenceFromClassAverage: lastScore - getLastDraftAverage(data, promptFilter),
				draftHistory: filteredEssays.flatMap((fe) => fe.drafts),
			};
		})
		.filter((summary) => summary.draftCount > 0)
		.sort((a, b) => a.writerFormalFullName.localeCompare(b.writerFormalFullName));
}

// Return filtered data for chart.
function toChartSummary(
	data: WriterEssayDraftHistoryViewModel[] | undefined,
	writerFilter: number,
	promptFilter: number
): WriterEssayDraftHistoryViewModel[] {
	if (!data) {
		return [];
	}

	const filteredWriters =
		writerFilter == 0
			? data
			: data.filter((writer) => Number(writer.writerId) === Number(writerFilter));

	return filteredWriters
		.map((writer) => {
			const essays = writer.essays ?? [];
			const filteredEssays =
				promptFilter == 0
					? essays
					: essays.filter((essay) => Number(essay.promptId) === Number(promptFilter));
			return { ...writer, essays: filteredEssays };
		})
		.filter((writer) => (writer.essays?.length ?? 0) > 0)
		.sort((a, b) => a.writerFormalFullName.localeCompare(b.writerFormalFullName));
}
